<script setup>
import { FlexRender } from '@tanstack/vue-table';
import SortButton from '../SortButton.vue';
import TruncatedCell from './TruncatedCell.vue';
import { computed, h } from 'vue';

const props = defineProps({
  table: {
    type: Object,
    required: true,
  },
  fixed: {
    type: Boolean,
    default: false,
  },
  type: {
    type: String,
    default: 'relaxed',
  },
  /**
   * Define se as células devem truncar texto longo
   */
  truncateCells: {
    type: Boolean,
    default: true,
  },
  /**
   * Comprimento máximo de caracteres antes de truncar
   */
  maxCellLength: {
    type: Number,
    default: 60,
  },
  /**
   * Colunas que devem ignorar o truncamento
   */
  excludeFromTruncate: {
    type: Array,
    default: () => ['actions'],
  },
  /**
   * Função que determina se uma linha está desabilitada
   */
  getRowDisabled: {
    type: Function,
    default: null,
  },
  /**
   * Função que determina se uma linha está selecionada
   */
  getRowSelected: {
    type: Function,
    default: null,
  }
});

const emit = defineEmits(['row-click']);

const isRelaxed = computed(() => props.type === 'relaxed');
const headerClass = computed(() =>
  isRelaxed.value
    ? 'first:rounded-bl-lg first:rounded-tl-lg last:rounded-br-lg last:rounded-tr-lg'
    : ''
);

/**
 * Função que determina se deve renderizar com TruncatedCell
 */
const shouldTruncate = (cell) => {
  if (!props.truncateCells) return false;
  
  // Não truncar se esta coluna está na lista de exclusão
  const columnId = cell.column.id;
  if (props.excludeFromTruncate.includes(columnId)) return false;
  
  return true;
};

/**
 * Renderiza o conteúdo da célula com ou sem truncamento
 */
const renderCellContent = (cell) => {
  const content = cell.getValue();
  
  if (shouldTruncate(cell) && content && typeof content === 'string') {
    return h(TruncatedCell, {
      content,
      maxLength: props.maxCellLength,
      maxLines: 2
    });
  }
  
  return h(FlexRender, {
    render: cell.column.columnDef.cell,
    props: cell.getContext()
  });
};

/**
 * Verifica se uma linha está desabilitada
 */
const isRowDisabled = (row) => {
  return props.getRowDisabled ? props.getRowDisabled(row.original) : false;
};

/**
 * Verifica se uma linha está selecionada
 */
const isRowSelected = (row) => {
  return props.getRowSelected ? props.getRowSelected(row) : false;
};

/**
 * Manipula o clique na linha
 */
const handleRowClick = (row, event) => {
  emit('row-click', row, event);
};
</script>

<template>
  <div class="overflow-x-auto w-full">
    <table :class="{ 'table-fixed': fixed, 'w-full': true }">
      <thead class="sticky top-0 z-10 bg-n-slate-1">
        <tr
          v-for="headerGroup in table.getHeaderGroups()"
          :key="headerGroup.id"
          class="rounded-xl"
        >
          <th
            v-for="header in headerGroup.headers"
            :key="header.id"
            :style="{
              width: header.getSize() ? `${header.getSize()}px` : undefined,
              minWidth: header.column.columnDef.minSize ? `${header.column.columnDef.minSize}px` : undefined,
              maxWidth: header.column.columnDef.maxSize ? `${header.column.columnDef.maxSize}px` : undefined
            }"
            class="text-left py-3 px-5 font-medium text-sm text-n-slate-12"
            :class="[
              headerClass,
              header.column.getCanSort() ? 'cursor-pointer hover:bg-n-slate-2' : ''
            ]"
            @click="header.column.getCanSort() && header.column.toggleSorting()"
          >
            <div
              v-if="!header.isPlaceholder"
              class="flex place-items-center gap-1"
            >
              <FlexRender
                :render="header.column.columnDef.header"
                :props="header.getContext()"
              />
              <SortButton v-if="header.column.getCanSort()" :header="header" />
            </div>
          </th>
        </tr>
      </thead>

      <tbody class="divide-y divide-n-slate-2">
        <tr
          v-for="row in table.getRowModel().rows"
          :key="row.id"
          :class="[
            'transition-colors cursor-pointer',
            isRowDisabled(row)
              ? 'hover:bg-n-slate-1'
              : isRowSelected(row)
                ? 'bg-n-woot-50 dark:bg-n-woot-800/20 hover:bg-n-woot-100 dark:hover:bg-n-woot-800/30'
                : 'hover:bg-n-slate-1'
          ]"
          @click="handleRowClick(row, $event)"
        >
          <td
            v-for="cell in row.getVisibleCells()"
            :key="cell.id"
            :class="[
              isRelaxed ? 'py-4 px-5' : 'py-2 px-5',
              'align-top',
              shouldTruncate(cell) ? 'max-w-[250px]' : '',
              isRowDisabled(row) && !['selection', 'actions'].includes(cell.column.id) ? 'opacity-40' : ''
            ]"
          >
            <component :is="renderCellContent(cell)" />
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>